// services/scoringService.ts
// This service will handle API calls to the scoring engine
import request from '@/helpers/request'

export const scoringService = {
  async extractUrlsWithRegex(postText: string): Promise<any> {
    try {
      const response = await request.post('/scoring/extract-regex', {
        postText: postText
      })
      return {
        success: response.data.success ?? false,
        urls: response.data.urls || [],
        count: response.data.count || 0,
        error: response.data.error
      }
    } catch (error) {
      console.error('Error extracting URLs with regex:', error)
      throw error
    }
  },

  async classifyPost(postText: string): Promise<any> {
    try {
      const response = await request.post('/scoring/classify-post', {
        postText: postText
      })
      return response.data
    } catch (error) {
      console.error('Error classifying post:', error)
      throw error
    }
  },

  async extractWithSpacy(postText: string, modelName: string = 'deal-post-model'): Promise<any> {
    try {
      const response = await request.post('/scoring/extract-spacy', {
        postText: postText,
        modelName: modelName
      })
      return {
        success: response.data.success ?? false,
        urls: response.data.urls || [],
        promoCodes: response.data.promoCodes || [],
        discountPercent: response.data.discountPercent,
        entities: response.data.entities || []
      }
    } catch (error) {
      console.error('Error extracting with spaCy:', error)
      throw error
    }
  },

  async extractWithGliner(postText: string, labels?: string[]): Promise<any> {
    try {
      const response = await request.post('/scoring/extract-gliner', {
        postText: postText,
        labels: labels
      })
      return {
        success: response.data.success ?? false,
        urls: response.data.urls || [],
        promoCodes: response.data.promoCodes || [],
        discountPercent: response.data.discountPercent,
        confidence: response.data.confidence || 0.0,
        entities: response.data.entities || []
      }
    } catch (error) {
      console.error('Error extracting with GLiNER:', error)
      throw error
    }
  },

  async expandUrl(url: string): Promise<any> {
    try {
      const response = await request.post('/scoring/expand-url', {
        url: url
      })
      return {
        success: response.data.success ?? false,
        originalUrl: response.data.originalUrl,
        finalUrl: response.data.finalUrl,
        error: response.data.error
      }
    } catch (error) {
      console.error('Error expanding URL:', error)
      throw error
    }
  },

  async getProductDetails(url: string): Promise<any> {
    try {
      const response = await request.post('/scoring/product-details', {
        url: url
      })
      return {
        success: response.data.success ?? false,
        error: response.data.error,
        retailer: response.data.retailer,
        asin: response.data.asin,
        title: response.data.title,
        brand: response.data.brand,
        price: response.data.price,
        category: response.data.category,
        productUrl: response.data.productUrl,
        imageUrls: response.data.imageUrls || [],
        features: response.data.features || [],
        rawJson: response.data.rawJson
      }
    } catch (error) {
      console.error('Error getting product details:', error)
      throw error
    }
  },

  async ingestDeal(deal: any) {
    try {
      const response = await request.post('/scoring/ingest', {
        title: deal.title,
        brand: deal.brand,
        retailer: deal.retailer,
        price: deal.price,
        url: deal.url
      })
      return response.data
    } catch (error) {
      console.error('Error ingesting deal:', error)
      throw error
    }
  },

  async normalizeDeal(deal: any) {
    try {
      const response = await request.post('/scoring/normalize', {
        title: deal.title,
        brand: deal.brand,
        retailer: deal.retailer
      })
      return response.data
    } catch (error) {
      console.error('Error normalizing deal:', error)
      throw error
    }
  },

  async categorize(canonicalProduct: any): Promise<any> {
    // TODO: Implement when Milestone 10 is complete
    return null
  },

  async assignAudience(canonicalProduct: any): Promise<any> {
    // TODO: Implement when Milestone 11 is complete
    return null
  },

  async calculatePriceIntelligence(canonicalKey: string, price: number): Promise<any> {
    // TODO: Implement when Milestone 12 is complete
    return null
  },

  async getBrandIntelligence(brand: string, categoryId: number): Promise<any> {
    // TODO: Implement when Milestone 13 is complete
    return null
  },

  async scoreDeal(dealId: number): Promise<any> {
    // TODO: Implement when Milestone 14 is complete
    return null
  },

  async applyTags(dealId: number): Promise<any> {
    // TODO: Implement when Milestone 15 is complete
    return null
  },

  async updateLeaderboards(dealId: number, score: number): Promise<any> {
    // TODO: Implement when Milestone 16 is complete
    return null
  },

  async indexDeal(dealId: number): Promise<any> {
    // TODO: Implement when Milestone 17 is complete
    return null
  },

  async processPostToDeal(postText: string, modelName?: string): Promise<any> {
    try {
      const response = await request.post('/ProcessPostToDeal', {
        postText: postText,
        modelName: modelName || 'deal-post-model'
      })
      return {
        success: response.data.success ?? false,
        dealInfo: response.data.dealInfo,
        steps: response.data.steps,
        error: response.data.error
      }
    } catch (error: any) {
      console.error('Error processing post to deal:', error)
      throw error
    }
  }
}
